/**
 * ErrorDialog — modal for critical blocking errors requiring user action.
 * Use for: auth expired, data loss risk, critical system failure.
 * Reads state from notificationStore.dialog.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNotificationStore } from '../../store/notificationStore';
import { StatusWarning as ShieldExclamationIcon, Close as CloseIcon } from '../icons';
import './ErrorDialog.css';

export const ErrorDialog = () => {
  const dialog = useNotificationStore((s) => s.dialog);
  const dismissDialog = useNotificationStore((s) => s.dismissDialog);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Focus trap + ESC handler
  useEffect(() => {
    if (!dialog?.open) return;

    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus first button inside dialog
    const firstBtn = dialogRef.current?.querySelector('button');
    firstBtn?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dialog.dismissible) {
        dismissDialog();
        return;
      }

      // Focus trap
      if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable?.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [dialog?.open, dialog?.dismissible, dismissDialog]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget && dialog?.dismissible) {
      dismissDialog();
    }
  }, [dialog?.dismissible, dismissDialog]);

  if (!dialog?.open) return null;

  return (
    <div
      className="error-dialog-overlay"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="error-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-message"
      >
        {dialog.dismissible && (
          <button
            className="error-dialog__close"
            onClick={dismissDialog}
            type="button"
            aria-label="Close dialog"
          >
            <CloseIcon />
          </button>
        )}

        <div className="error-dialog__icon">
          <ShieldExclamationIcon size={24} />
        </div>

        <h2 id="error-dialog-title" className="error-dialog__title">
          {dialog.title}
        </h2>

        <p id="error-dialog-message" className="error-dialog__message">
          {dialog.message}
        </p>

        {(dialog.code || dialog.correlationId) && (
          <div className="error-dialog__meta">
            {dialog.code && (
              <span className="error-dialog__code">{dialog.code}</span>
            )}
            {dialog.correlationId && (
              <span className="error-dialog__code">Ref: {dialog.correlationId}</span>
            )}
          </div>
        )}

        <div className="error-dialog__actions">
          {dialog.secondaryAction && (
            <button
              className="error-dialog__btn error-dialog__btn--secondary"
              onClick={dialog.secondaryAction.onClick}
              type="button"
            >
              {dialog.secondaryAction.label}
            </button>
          )}
          <button
            className="error-dialog__btn error-dialog__btn--primary"
            onClick={dialog.primaryAction.onClick}
            type="button"
          >
            {dialog.primaryAction.label}
          </button>
        </div>
      </div>
    </div>
  );
};
